import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { MessageSquare, Users, Calendar, TrendingUp, Clock } from 'lucide-react';
import type { CommentListResponse } from '../../services/productService';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

interface CommentStatsProps {
  data: CommentListResponse | undefined;
  className?: string;
}

/**
 * CommentStats Component
 *
 * Displays comprehensive statistics about product comments:
 * - Total comments count
 * - Top-level comments vs replies
 * - Recent activity indicators
 * - Engagement metrics
 */
export function CommentStats({ data, className }: CommentStatsProps) {
  // Get translations
  const { getCommentsText } = useProductDetailPageTranslations();

  const { total_comments = 0, top_level_comments = 0, results = [] } = data || {};

  // Calculate actual replies count by recursively counting all replies
  const countAllReplies = (comments: typeof results): number => {
    let count = 0;
    for (const comment of comments) {
      if (comment.replies && comment.replies.length > 0) {
        count += comment.replies.length;
        count += countAllReplies(comment.replies);
      }
    }
    return count;
  };

  // Calculate derived statistics
  const actualRepliesCount = countAllReplies(results);
  const averageRepliesPerComment =
    top_level_comments > 0 ? Math.round((actualRepliesCount / top_level_comments) * 10) / 10 : 0;

  // Calculate recent activity (comments in last 24 hours)
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  // Count recent comments recursively including replies
  const countRecentComments = (comments: typeof results): number => {
    let count = 0;
    for (const comment of comments) {
      if (new Date(comment.created_at) > yesterday) {
        count++;
      }
      if (comment.replies && comment.replies.length > 0) {
        count += countRecentComments(comment.replies);
      }
    }
    return count;
  };

  const recentComments = countRecentComments(results);

  // Get most active contributors (including replies)
  const getAllComments = (comments: typeof results): typeof results => {
    let allComments = [...comments];
    for (const comment of comments) {
      if (comment.replies && comment.replies.length > 0) {
        allComments = allComments.concat(getAllComments(comment.replies));
      }
    }
    return allComments;
  };

  const allComments = getAllComments(results);
  const authorCounts = allComments.reduce(
    (acc, comment) => {
      const authorName = comment.author_full_name;
      acc[authorName] = (acc[authorName] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const topContributors = Object.entries(authorCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const stats = [
    {
      icon: MessageSquare,
      label: getCommentsText('stats.cards.totalComments.label'),
      value: total_comments,
      description: getCommentsText('stats.cards.totalComments.description'),
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: Users,
      label: getCommentsText('stats.cards.discussions.label'),
      value: top_level_comments,
      description: getCommentsText('stats.cards.discussions.description'),
      color: 'text-green-600 dark:text-green-400',
    },
    {
      icon: TrendingUp,
      label: getCommentsText('stats.cards.replies.label'),
      value: actualRepliesCount,
      description: getCommentsText('stats.cards.replies.description', {
        average: averageRepliesPerComment,
      }),
      color: 'text-purple-600 dark:text-purple-400',
    },
    {
      icon: Clock,
      label: getCommentsText('stats.cards.recentActivity.label'),
      value: recentComments,
      description: getCommentsText('stats.cards.recentActivity.description'),
      color: 'text-orange-600 dark:text-orange-400',
    },
  ];

  if (total_comments === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Statistics Grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map(stat => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="p-4">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3">
                  <div className={`rounded-lg bg-muted p-2 ${stat.color}`}>
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-bold">{stat.value}</span>
                      {stat.icon === Clock && recentComments > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {getCommentsText('stats.cards.recentActivity.badge')}
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-sm font-medium text-muted-foreground">
                      {stat.label}
                    </p>
                    <p className="text-xs text-muted-foreground">{stat.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Top Contributors */}
      {topContributors.length > 0 && (
        <Card className="p-4">
          <CardContent className="p-0">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-medium">{getCommentsText('stats.contributors.title')}</h4>
              <Badge variant="outline" className="text-xs">
                {getCommentsText('stats.contributors.participants', {
                  count: Object.keys(authorCounts).length,
                })}
              </Badge>
            </div>
            <div className="space-y-2">
              {topContributors.map(([authorName, count], index) => (
                <div key={authorName} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="flex size-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                      {index + 1}
                    </div>
                    <span className="truncate text-sm font-medium">{authorName}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {getCommentsText('stats.contributors.commentsCount', {
                      count,
                    })}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Engagement Summary */}
      {total_comments > 0 && (
        <Card className="p-4">
          <CardContent className="p-0">
            <h4 className="mb-3 text-sm font-medium">
              {getCommentsText('stats.engagement.title')}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">
                  {getCommentsText('stats.engagement.discussionRate.label')}
                </span>
                <div className="font-medium">
                  {top_level_comments > 0
                    ? getCommentsText('stats.engagement.discussionRate.value', {
                        percentage: Math.round((actualRepliesCount / top_level_comments) * 100),
                      })
                    : getCommentsText('stats.engagement.discussionRate.noDiscussions')}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {getCommentsText('stats.engagement.activityLevel.label')}
                </span>
                <div className="font-medium">
                  {recentComments > 0
                    ? getCommentsText('stats.engagement.activityLevel.recent', {
                        count: recentComments,
                      })
                    : getCommentsText('stats.engagement.activityLevel.noActivity')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
